/* this might be a server component but is used by CmsTable */
'use client';

import React from 'react';
import { cn } from '../../utils/cn/cn';

const Table = ({
  className,
  children,
  ref,
  ...props
}: React.HTMLAttributes<HTMLTableElement> & {
  ref?: React.Ref<HTMLTableElement>;
}) => (
  <div className="relative w-full overflow-auto">
    <table
      ref={ref}
      className={cn('text-size-body-sm w-max caption-bottom text-left', className)}
      {...props}
    >
      {children}
    </table>
  </div>
);

const TableHeader = ({
  className,
  ref,
  ...props
}: React.HTMLAttributes<HTMLTableSectionElement> & {
  ref?: React.Ref<HTMLTableSectionElement>;
}) => (
  <thead
    ref={ref}
    className={cn(
      '[&_th:first-child]:rounded-tl-md [&_th:last-child]:rounded-tr-md [&_tr]:border-b',
      className,
    )}
    {...props}
  />
);

const TableBody = ({
  className,
  ref,
  ...props
}: React.HTMLAttributes<HTMLTableSectionElement> & {
  ref?: React.Ref<HTMLTableSectionElement>;
}) => <tbody ref={ref} className={cn('[&_tr:last-child]:border-0', className)} {...props} />;

const TableFooter = ({
  className,
  ref,
  ...props
}: React.HTMLAttributes<HTMLTableSectionElement> & {
  ref?: React.Ref<HTMLTableSectionElement>;
}) => (
  <tfoot
    ref={ref}
    className={cn('border-t font-medium [&>tr]:last:border-b-0', className)}
    {...props}
  />
);

const TableRow = ({
  className,
  ref,
  ...props
}: React.HTMLAttributes<HTMLTableRowElement> & { ref?: React.Ref<HTMLTableRowElement> }) => (
  <tr ref={ref} className={cn('border-b [&_td:first-child]:font-bold', className)} {...props} />
);

const TableHead = ({
  className,
  ref,
  ...props
}: React.ThHTMLAttributes<HTMLTableCellElement> & { ref?: React.Ref<HTMLTableCellElement> }) => (
  <th
    ref={ref}
    className={cn(
      'bg-color-grey-light h-4.5 px-1.5 py-1 text-left align-middle font-medium',
      className,
    )}
    {...props}
  />
);

const TableCell = ({
  className,
  ref,
  ...props
}: React.TdHTMLAttributes<HTMLTableCellElement> & { ref?: React.Ref<HTMLTableCellElement> }) => (
  <td
    ref={ref}
    className={cn(
      'border-color-grey-light border-2 p-4 px-1.5 py-1 align-middle [&:has([role=checkbox])]:pr-0',
      className,
    )}
    {...props}
  />
);

const TableCaption = ({
  className,
  ref,
  ...props
}: React.HTMLAttributes<HTMLTableCaptionElement> & {
  ref?: React.Ref<HTMLTableCaptionElement>;
}) => (
  <caption
    ref={ref}
    className={cn(
      'text-size-body-xs text-color-text-darktransparent m-0 mt-2 text-left',
      className,
    )}
    {...props}
  />
);

export { Table, TableHeader, TableBody, TableFooter, TableHead, TableRow, TableCell, TableCaption };
